import { useState, useCallback } from "react";
import { Megaphone, Plus, Pin } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { FilterBar } from "@/components/admin/shared/FilterBar";
import type { FilterConfig } from "@/components/admin/shared/FilterBar";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  announcementsApi,
  type Announcement,
  type AnnouncementPriority,
  type CreateAnnouncementInput,
} from "@/lib/admin-api/announcements";

// ---------------------------------------------------------------------------
// Filter configs
// ---------------------------------------------------------------------------

const FILTER_CONFIGS: readonly FilterConfig[] = [
  {
    key: "priority",
    label: "Priorité",
    type: "select",
    options: [
      { value: "haute", label: "Haute" },
      { value: "normale", label: "Normale" },
    ],
  },
  {
    key: "is_pinned",
    label: "Épinglé",
    type: "select",
    options: [
      { value: "true", label: "Épinglé" },
      { value: "false", label: "Non épinglé" },
    ],
  },
];

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatDate(d: string): string {
  return new Date(d).toLocaleDateString("fr-SN");
}

// ---------------------------------------------------------------------------
// Create / edit modal
// ---------------------------------------------------------------------------

interface AnnouncementFormModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly isSaving: boolean;
  readonly onSave: (input: CreateAnnouncementInput) => Promise<void>;
  readonly initial?: Announcement;
}

function AnnouncementFormModal({
  open,
  onClose,
  isSaving,
  onSave,
  initial,
}: AnnouncementFormModalProps) {
  const [title, setTitle] = useState(initial?.title ?? "");
  const [body, setBody] = useState(initial?.body ?? "");
  const [priority, setPriority] = useState<AnnouncementPriority>(initial?.priority ?? "normale");
  const [isPinned, setIsPinned] = useState(initial?.is_pinned ?? false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    if (!initial) {
      setTitle("");
      setBody("");
      setPriority("normale");
      setIsPinned(false);
    }
    setError(null);
    onClose();
  }, [onClose, initial]);

  const handleSubmit = useCallback(async () => {
    if (!title.trim()) {
      setError("Le titre est requis.");
      return;
    }
    if (!body.trim()) {
      setError("Le contenu est requis.");
      return;
    }
    setError(null);
    try {
      await onSave({ title: title.trim(), body: body.trim(), priority, is_pinned: isPinned });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue.");
    }
  }, [title, body, priority, isPinned, onSave, handleClose]);

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      title={initial ? "Modifier l'annonce" : "Nouvelle annonce"}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isSaving}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
        )}

        <div>
          <label className="mb-1 block text-sm font-medium text-stone-700">
            Titre <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            placeholder="Titre de l'annonce"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-stone-700">
            Contenu <span className="text-red-500">*</span>
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            placeholder="Contenu de l'annonce…"
          />
        </div>

        <div className="flex items-center gap-6">
          <div>
            <label className="mb-1 block text-sm font-medium text-stone-700">Priorité</label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value as AnnouncementPriority)}
              className="rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            >
              <option value="normale">Normale</option>
              <option value="haute">Haute</option>
            </select>
          </div>

          <label className="flex cursor-pointer items-center gap-2 text-sm text-stone-700">
            <input
              type="checkbox"
              checked={isPinned}
              onChange={(e) => setIsPinned(e.target.checked)}
              className="rounded border-stone-300"
            />
            Épingler l'annonce
          </label>
        </div>
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Announcements() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, unknown>>({});
  const [showCreate, setShowCreate] = useState(false);

  const queryClient = useQueryClient();

  const queryParams = {
    page,
    limit: 20,
    priority: (filters.priority as AnnouncementPriority) ?? undefined,
    is_pinned:
      filters.is_pinned != null
        ? filters.is_pinned === "true"
        : undefined,
  };

  const { data, isLoading } = useQuery({
    queryKey: ["admin-announcements", queryParams],
    queryFn: () => announcementsApi.list(queryParams),
    staleTime: 30_000,
  });

  const createMutation = useMutation({
    mutationFn: (input: CreateAnnouncementInput) => announcementsApi.create(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-announcements"] }),
  });

  const announcements = data?.announcements ?? [];
  const pagination = data?.pagination;

  const handleFilterChange = useCallback((key: string, value: unknown) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const handleCreate = useCallback(
    async (input: CreateAnnouncementInput) => {
      await createMutation.mutateAsync(input);
    },
    [createMutation],
  );

  const columns: readonly ColumnDef<Announcement>[] = [
    {
      id: "title",
      header: "Titre",
      cell: (row) => (
        <div className="flex items-center gap-2">
          {row.is_pinned && (
            <Pin className="h-3.5 w-3.5 shrink-0 text-amber-500" />
          )}
          <span className="font-medium text-stone-900">{row.title}</span>
        </div>
      ),
    },
    {
      id: "priority",
      header: "Priorité",
      cell: (row) => (
        <span
          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${
            row.priority === "haute"
              ? "border-red-200 bg-red-100 text-red-800"
              : "border-stone-200 bg-stone-100 text-stone-600"
          }`}
        >
          {row.priority === "haute" ? "Haute" : "Normale"}
        </span>
      ),
    },
    {
      id: "audience",
      header: "Audience",
      cell: (row) => (
        <span className="text-sm text-stone-500">
          {row.target_departments && row.target_departments.length > 0
            ? row.target_departments.join(", ")
            : "Tous"}
        </span>
      ),
    },
    {
      id: "read_count",
      header: "Vues",
      cell: (row) => (
        <span className="text-sm text-stone-600">
          {row.read_count}/{row.total_targets}
        </span>
      ),
    },
    {
      id: "author_name",
      header: "Auteur",
      accessorKey: "author_name",
    },
    {
      id: "created_at",
      header: "Créée le",
      cell: (row) => (
        <span className="text-xs text-stone-400">{formatDate(row.created_at)}</span>
      ),
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
              <Megaphone className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Annonces internes
              </h1>
              <p className="text-sm text-stone-500">
                Communication équipe
              </p>
            </div>
          </div>
          <Button onClick={() => setShowCreate(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Nouvelle annonce
          </Button>
        </div>

        {/* Filters */}
        <div className="rounded-lg border border-stone-200 bg-white p-4">
          <FilterBar
            filters={FILTER_CONFIGS}
            values={filters}
            onChange={handleFilterChange}
          />
        </div>

        {/* Table */}
        <AdminDataTable
          columns={columns}
          data={announcements}
          pagination={
            pagination
              ? { page: pagination.page, limit: pagination.limit, total: pagination.total }
              : undefined
          }
          onPageChange={setPage}
          isLoading={isLoading}
          emptyMessage="Aucune annonce trouvée."
        />
      </div>

      <AnnouncementFormModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        isSaving={createMutation.isPending}
        onSave={handleCreate}
      />
    </AdminLayout>
  );
}
